<template>
  <tr>
    <td class="text-center">
      {{ batch.operation.city }} {{ batch.operation.state ? batch.operation.state.value : "" }}
      <div v-if="batch.operation && batch.operation.state && batch.operation.state.display_med_rec_designations" class="text-center">
        <div v-if="batch.operation.industry == 'Medical'" class="badge-med-rec badge-medical" style="width: 47px; padding: 2px">Med</div>
        <div v-else-if="batch.operation.industry == 'Recreational'" class="badge-med-rec badge-recreational" style="width: 47px; padding: 2px">
          Rec
        </div>
      </div>
    </td>
    <td class="text-center">
      {{ batch.name }}
    </td>
    <td class="text-center">
      <span v-if="batch.tiered_listing_price" class="font-weight-normal">
        <span style="text-decoration: line-through" class="text-danger font-weight-light">${{ (batch.listing_price / 100).toLocaleString() }}</span>
        ${{ (batch.tiered_listing_price / 100).toLocaleString() }}
      </span>
      <span v-else>${{ (batch.listing_price / 100).toLocaleString() }}</span>
      <div>
        <span v-for="value in getUnitMeasurementPriceBreakdown(product, batch)" :key="value" class="badge border mr-1 text-small">
          {{ value }}
        </span>
      </div>

      <p v-if="isTheSeller && batch.minimum_sales_price" class="text-danger small m-0">
        Min Price: ${{ (batch.minimum_sales_price / 100).toLocaleString() }}
      </p>
    </td>
    <td class="text-center">
      <p class="mb-0">{{ batch.disable_inventory_tracking ? "Unlimited" : batch.quantity }} {{ getUnit(product).name }}</p>

      <div class="text-sm">
        <p class="mb-0">
          {{ getPackageCaseOptions(product) }}
        </p>
        <p class="mb-0">
          {{ getUnitSize(product) }}
        </p>
        <p v-if="product.packaged_unit_size" class="mb-0">Package Size: {{ product.packaged_unit_size }}</p>
        <p v-if="batch.back_order" :id="`backorder${batch.id}`" class="cursor-default mb-0">
          (On Pre-Order)
          <BTooltip :target="`backorder${batch.id}`" triggers="hover">
            This product is on preorder. You may order it and the seller will fulfill the order when it is available.
            {{ batch.in_stock_expected_date && `(Expected to be in stock on ${batch.in_stock_expected_date})` }}
          </BTooltip>
        </p>
        <p v-if="batch.restricted" :id="`restricted${batch.id}`" class="cursor-default mb-0">
          (Restricted)
          <i class="fas fa-question text-info" />
          <BTooltip :target="`restricted${batch.id}`" triggers="hover">
            This product is restricted but you are authorized to add it to orders.
          </BTooltip>
        </p>
        <p v-if="batch.restricted && batch.hold" :id="`restrictedHold${batch.id}`" class="cursor-default text-danger mb-0">
          (Restricted On Deck)
          <i class="fas fa-question text-info" />
          <BTooltip :target="`restrictedHold${batch.id}`" triggers="hover">
            This product is restricted and on deck, but on deck functionality does not work with restricted items.
          </BTooltip>
        </p>
        <span v-if="quantityInCart && quantityInCart != 0 ? true : false">({{ quantityInCart }} already in <i class="fa fa-shopping-cart" />)</span>
        <p v-if="sampleInCart && sampleInCart != 0 ? true : false" class="mb-0">(Sample in <i class="fa fa-shopping-cart" />)</p>
      </div>
    </td>
    <!-- <td class="text-center">
      <TruncateShowHide
        :cut-length="150"
        :the-text="batch.description"
      />
    </td> -->
    <td class="text-center">
      <div v-if="batch.hold && !onDeckOverride">...</div>
      <div v-else>
        {{
          `${batch.predominate_canabinoid_min_or_only ? batch.predominate_canabinoid_min_or_only : ""}${
            batch.predominate_canabinoid_max ? " - " + batch.predominate_canabinoid_max : ""
          }${batch.predominate_canabinoid_unit ? batch.predominate_canabinoid_unit : ""} ${product.predominateCanabinoidAbbreviation} `
        }}
      </div>
    </td>
    <td v-if="showAgingField(product.category_name, 'harvest_date')" class="text-center">
      {{ batch.harvest_date ? dateLocalized(batch.harvest_date) : "" }}
    </td>
    <td v-if="showAgingField(product.category_name, 'extraction_date')" class="text-center">
      {{ batch.extraction_date ? dateLocalized(batch.extraction_date) : "" }}
    </td>
    <td v-if="showAgingField(product.category_name, 'production_date')" class="text-center">
      {{ batch.production_date ? dateLocalized(batch.production_date) : "" }}
    </td>
    <td v-if="showAgingField(product.category_name, 'best_by_date')" class="text-center">
      {{ batch.best_by_date ? dateLocalized(batch.best_by_date) : "" }}
    </td>
    <td class="text-center" style="max-width: 100px">
      <div v-if="batch.hold && !onDeckOverride" :id="`holdinputsection${batch.id}`" class="cursor-default">
        <p class="mb-0">
          On Deck <i class="fas fa-question text-info" />

          <BTooltip :target="`holdinputsection${batch.id}`" triggers="hover" placement="left">
            This Batch is On Deck and can be ordered once available batches have sold out.
          </BTooltip>
        </p>
      </div>
      <div v-else>
        <BushelBtn
          v-if="product.category_name == 'Pack To Order'"
          class="btn-block"
          variant="success"
          @click="$emit('launchPTOModal', { product: product, batch: batch })"
        >
          <i class="fas fa-calculator" />
        </BushelBtn>
        <div v-else class="d-flex">
          <BushelTextInput
            v-model="productToAddToStore.quantity"
            type="number"
            class="flex-grow-1"
            style="min-width: 50px"
            thin
            :maxlength="10"
            hide-label
            :error="errorInInputField"
            :number-type-allow-decimal="categoryTypeCanHaveDecimal(product.category_name, product)"
            :number-type-max-precision="6"
            @input-blurred="quantityEnteredCheck"
          />
          <BushelBtn
            variant="success"
            :disabled="quantitySubmitDisabled"
            class="btn-block ml-2"
            style="min-width: 38px"
            inline-form-class
            @click="addToCartTriggered"
          >
            <i class="fas fa-plus" />
          </BushelBtn>
        </div>
        <BushelBtn
          v-if="batch.allow_samples"
          :disabled="sampleInCart && sampleInCart != 0 ? true : false"
          class="btn-block"
          variant="success"
          @click="sampleRequestTriggered"
        >
          {{ sampleInCart && sampleInCart != 0 ? "Sample Requested" : "Request Sample" }}
        </BushelBtn>
      </div>
    </td>
  </tr>
</template>

<script>
import { dateLocalized } from "@/services/filters";

import { categoryTypeCanHaveDecimal } from "@/services/inventory/inventory";
// import TruncateShowHide from '@/components/common/TruncateShowHide';
import { getUnit, getPackageCaseOptions, getUnitSize, getUnitMeasurementPriceBreakdown } from "@/services/storefront/storefront";

export default {
  components: {},
  props: {
    batch: { type: Object, required: true },
    product: { type: Object, required: true },
    cartItems: { type: Array, required: true },
    isTheSeller: { type: Boolean, required: true },
    showAgingFields: { type: Boolean, default: false },
  },
  emits: ["addedProduct", "launchPTOModal"],
  data() {
    return {
      productToAddToStore: {
        batch_id: this.batch.id,
        quantity: null,
      },
    };
  },
  computed: {
    sampleInCart() {
      if (this.cartItems && this.cartItems.length) {
        const foundItem = this.cartItems.find((item) => item.batch_id == this.batch.id && item.sample);
        if (foundItem) {
          return `${foundItem.quantity.toString()}`;
        }
      }
      return 0;
    },

    // if the current batch is on deck, and all the batches that are active with a sort_order less than the current batch's sort_order are in cart,
    // then this batch is the next batch to be sold and should be able to be added to cart
    // check for cart items because if no cart items than it will never be overriden, and also because
    // cart items will always be empty from order modify screen and we don't want this to apply there
    onDeckOverride() {
      if (this.batch.hold && this.cartItems && this.cartItems.length) {
        // get all the batches
        const batchesBeforeCurrent = this.product.batches.filter((batch) => batch.sort_order < this.batch.sort_order);

        // get all the items that don't have all their quantity tied up in cart

        let priorBatchQuantityAvailable = false;

        for (let i = 0; i < batchesBeforeCurrent.length; i += 1) {
          if (!priorBatchQuantityAvailable) {
            const foundCartItem = this.cartItems.find((item) => item.batch_id == batchesBeforeCurrent[i].id && !item.sample);
            if (!foundCartItem) {
              // this means that there is an item with a sort order above checking item that is not in cart.
              priorBatchQuantityAvailable = true;
            } else if (foundCartItem.quantity < batchesBeforeCurrent[i].quantity) {
              // if there is a batch that still has quantity remaining
              priorBatchQuantityAvailable = true;
            }
          }
        }
        if (priorBatchQuantityAvailable) {
          return false;
        }

        return true;
      }
      return false;
    },

    quantityInCart() {
      if (this.cartItems && this.cartItems.length) {
        const foundItem = this.cartItems.find((item) => item.batch_id == this.batch.id && !item.sample);
        if (foundItem) {
          return `${foundItem.quantity.toString()}`;
        }
      }
      return 0;
    },
    errorInInputField() {
      if (this.productToAddToStore.quantity && Number.isNaN(Number(this.productToAddToStore.quantity))) {
        return true;
      }
      if (
        this.productToAddToStore.quantity &&
        this.productToAddToStore.quantity % 1 != 0 &&
        !this.categoryTypeCanHaveDecimal(this.product.category_name, this.product)
      ) {
        return true;
      }
      const { quantityInCart } = this;
      if (!this.batch.disable_inventory_tracking && Number(quantityInCart) + Number(this.productToAddToStore.quantity) > this.batch.quantity) {
        return true;
      }
      return false;
    },
    quantitySubmitDisabled() {
      if (!this.productToAddToStore || !this.productToAddToStore.quantity) {
        return true;
      }
      if (this.batch.hold && !this.onDeckOverride) {
        return true;
      }
      if (this.errorInInputField) {
        return true;
      }
      if (!this.batch.thc_limit) {
        return true;
      }

      return false;
    },
  },
  methods: {
    dateLocalized,
    categoryTypeCanHaveDecimal,
    getUnit,
    getPackageCaseOptions,
    getUnitSize,
    getUnitMeasurementPriceBreakdown,

    resetProductStoreForm() {
      this.productToAddToStore.quantity = null;
    },
    quantityEnteredCheck() {
      let newQuantity = Number(this.productToAddToStore.quantity);

      if (newQuantity + this.quantityInCart > this.batch.quantity && !this.batch.disable_inventory_tracking) {
        newQuantity = this.batch.quantity - this.quantityInCart;
      }
      if (Number.isNaN(Number(newQuantity))) {
        newQuantity = 0;
      }
      if (newQuantity && newQuantity % 1 != 0 && !this.categoryTypeCanHaveDecimal(this.product.category_name, this.product)) {
        newQuantity = parseInt(newQuantity, 10);
      }
      if (newQuantity != 0) {
        this.productToAddToStore.quantity = newQuantity;
      } else {
        this.productToAddToStore.quantity = null;
      }
    },

    addToCartTriggered() {
      if (this.productToAddToStore.quantity) {
        this.productAdded = true;
        this.$emit("addedProduct", {
          batch_id: this.batch.id,
          quantity: this.productToAddToStore.quantity,
          sample: false,
          product_id: this.product.id,
          company_id: this.product.company_id,
        });
        this.resetProductStoreForm();
      }
    },
    sampleRequestTriggered() {
      this.sampleRequested = true;
      // dont really need sample_size, will look it up in php
      this.$emit("addedProduct", {
        batch_id: this.batch.id,
        quantity: 1,
        sample: true,
        product_id: this.product.id,
        company_id: this.product.company_id,
      });
    },
    showAgingField(productCategory, field) {
      const agingDates = {
        harvest_date: ["flower", "pack to order", "plant material", "prepack", "seed", "preroll"],
        extraction_date: ["extract", "bulk extract", "cartridge"],
        production_date: ["tincture"],
        best_by_date: ["edibles & drink", "topicals & wellness"],
      };

      if (this.showAgingFields && productCategory && agingDates[field].includes(productCategory.toLowerCase())) {
        return true;
      }

      return false;
    },
  },
};
</script>
